import React, { useCallback, useContext, useReducer } from "react";
import {
  initialAlert,
  initialOrganizeState,
  OrganizeContext,
} from "./organize-context";
import {
  isAnthology,
  isAnthologyOrCollection,
  isCollection,
} from "./organizeUtil";
import {
  any,
  append,
  concat,
  differenceWith,
  equals,
  filter,
  isEmpty,
  prop,
  propEq,
  reject,
  uniqBy,
} from "ramda";
import { deduplicate } from "../../util/mediaUtil";
import { useSnackAlert } from "../../common/use-snackAlert";
import { Button } from "@mui/material";

const removeList = differenceWith((selected, list) => selected.id === list.id);

const actionTypes = {
  update_active_selected_media_type: "update_active_selected_media_type",
  update_alert: "update_alert",
  update_current_collection_content_list:
    "update_current_collection_content_list",
  update_drag_drop_dialog: "update_drag_drop_dialog",
  update_drag_source_id: "update_drag_source_id",
  update_drag_action: "update_drag_action",
  update_drag_source_parent: "update_drag_source_parent",
  update_drop_handler: "update_drop_handler",
  update_drop_items: "update_drop_items",
  update_drop_target: "update_drop_target",
  update_include_dialog: "update_include_dialog",
  update_is_dragging: "update_is_dragging",
  update_selected_content_groups: "update_selected_content_groups",
  update_selected_content: "update_selected_content",
  update_expanded: "update_expanded",
};

const organizeMediaReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.update_active_selected_media_type: {
      return {
        ...state,
        activeSelectedMediaType: action.activeSelectedMediaType,
      };
    }

    case actionTypes.update_alert: {
      return {
        ...state,
        alert: action.alert,
      };
    }

    case actionTypes.update_current_collection_content_list: {
      return {
        ...state,
        currentCollectionContentList: action.currentCollectionContentList,
      };
    }

    case actionTypes.update_drag_action: {
      return { ...state, dragAction: action.dragAction };
    }

    case actionTypes.update_drag_source_id: {
      return { ...state, dragDropDialog: action.dragDropDialog };
    }

    case actionTypes.update_drag_source_parent: {
      return { ...state, dragSourceParent: action.dragSourceParent };
    }

    case actionTypes.update_drop_handler: {
      return { ...state, dropHandler: action.dropHandler };
    }

    case actionTypes.update_drop_items: {
      return { ...state, dropItems: action.dropItems };
    }

    case actionTypes.update_drop_target: {
      return { ...state, dropTarget: action.dropTarget };
    }

    case actionTypes.update_include_dialog: {
      return {
        ...state,
        includeDialog: action.includeDialog,
      };
    }

    case actionTypes.update_is_dragging: {
      return {
        ...state,
        isDragging: action.isDragging,
      };
    }

    case actionTypes.update_selected_content_groups: {
      return {
        ...state,
        selectedContentGroups: action.selectedContentGroups,
      };
    }

    case actionTypes.update_selected_content: {
      return {
        ...state,
        selectedContent: action.selectedContent,
      };
    }

    case actionTypes.update_expanded: {
      return {
        ...state,
        expanded: action.expanded,
      };
    }

    default: {
      throw new Error(`Unhandled type in organizeMediaReducer: ${action.type}`);
    }
  }
};

const OrganizeProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    organizeMediaReducer,
    initialOrganizeState,
  );

  const { SnackAlert, showAlert, closeAlert } = useSnackAlert();

  const moveNotAllowed = useCallback(
    ({ group }) => {
      const { action, activeType, selected } = state.includeDialog;
      if (equals(activeType, "content")) {
        return equals(group.mediaType, "anthology");
      }

      if (equals(activeType, "contentGroup")) {
        if (any(isAnthology, selected)) {
          return isAnthologyOrCollection(group);
        }

        if (any(isCollection, selected)) {
          if (action === "copy") {
            return isAnthologyOrCollection(group);
          } else {
            return isCollection(group);
          }
        }
      }

      return false;
    },
    [state.includeDialog],
  );

  const updateActiveSelectedMediaType = useCallback(activeSelectedMediaType => {
    dispatch({
      type: actionTypes.update_active_selected_media_type,
      activeSelectedMediaType,
    });
  }, []);

  const updateDragAction = useCallback(action => {
    dispatch({
      type: actionTypes.update_drag_action,
      dragAction: action,
    });
  }, []);

  const updateDragSourceId = useCallback(dragSourceId => {
    dispatch({
      type: actionTypes.update_drag_source_id,
      dragSourceId,
    });
  }, []);

  const updateDragSourceParent = useCallback(dragSourceParent => {
    dispatch({
      type: actionTypes.update_drag_source_parent,
      dragSourceParent,
    });
  }, []);

  const updateDropHandler = useCallback(dropHandler => {
    dispatch({
      type: actionTypes.update_drop_handler,
      dropHandler,
    });
  }, []);

  const updateDropItems = useCallback(dropItems => {
    dispatch({
      type: actionTypes.update_drop_items,
      dropItems,
    });
  }, []);

  const updateDropTarget = useCallback(dropTarget => {
    dispatch({
      type: actionTypes.update_drop_target,
      dropTarget,
    });
  }, []);

  const updateIncludeDialog = useCallback(includeDialog => {
    dispatch({
      type: actionTypes.update_include_dialog,
      includeDialog,
    });
  }, []);

  const updateIsDragging = useCallback(isDragging => {
    dispatch({
      type: actionTypes.update_is_dragging,
      isDragging,
    });
  }, []);

  const removeAllSelectedContentGroups = useCallback(() => {
    dispatch({
      type: actionTypes.update_selected_content_groups,
      selectedContentGroups: [],
    });
  }, []);

  const updateSelectedContentGroups = useCallback(
    group => {
      if (any(propEq("id", group.id), state.selectedContentGroups)) {
        const updated = reject(
          propEq("id", group.id),
          state.selectedContentGroups,
        );
        dispatch({
          type: actionTypes.update_selected_content_groups,
          selectedContentGroups: updated,
        });
      } else {
        const updated = append(group, state.selectedContentGroups);
        dispatch({
          type: actionTypes.update_selected_content_groups,
          selectedContentGroups: updated,
        });
      }
    },
    [state.selectedContentGroups],
  );

  const updateSelectedContentGroupsWithList = useCallback(
    contentGroupsList => {
      const updated = uniqBy(
        prop("id"),
        concat(state.selectedContentGroups, contentGroupsList),
      );

      dispatch({
        type: actionTypes.update_selected_content_groups,
        selectedContentGroups: updated,
      });
    },
    [state.selectedContentGroups],
  );

  const selectAllContentGroups = useCallback(
    (target, contentGroupsList) => {
      if (target.checked && isEmpty(state.selectedContentGroups)) {
        const updated = uniqBy(
          prop("id"),
          concat(state.selectedContentGroups, contentGroupsList),
        );

        dispatch({
          type: actionTypes.update_selected_content_groups,
          selectedContentGroups: updated,
        });
      } else {
        dispatch({
          type: actionTypes.update_selected_content_groups,
          selectedContentGroups: [],
        });
      }
    },
    [state.selectedContentGroups],
  );

  const updateSelectedContent = useCallback(
    update => {
      if (any(propEq("id", update.id), state.selectedContent)) {
        const updated = reject(propEq("id", update.id), state.selectedContent);
        dispatch({
          type: actionTypes.update_selected_content,
          selectedContent: updated,
        });
      } else {
        const selected = concat([update], state.selectedContent);
        const updated = uniqBy(prop("id"), selected);

        dispatch({
          type: actionTypes.update_selected_content,
          selectedContent: updated,
        });
      }
    },
    [state.selectedContent],
  );

  // const updateSelectedContextMenuItem = useCallback(
  //   selected => {
  //     const item = {
  //       id: selected.child.id,
  //       parent: selected.parent,
  //       prevMediaList: selected.prevMediaList,
  //     };

  //     // if item is already selected, then return
  //     // so that item remains highlighted after context menu click
  //     const alreadySelected = item => {
  //       return (
  //         any(propEq("id", item.id), state.selectedAnthologies) ||
  //         any(propEq("id", item.id), state.selectedCollections) ||
  //         any(propEq("id", item.id), state.selectedContent)
  //       );
  //     };

  //     if (!alreadySelected(item)) {
  //       switch (selected.child.mediaType) {
  //         case "anthology":
  //           updateSelectedAnthologies(item);
  //           return;
  //         case "collection":
  //           updateSelectedCollections(item);
  //           return;
  //         case "content":
  //           updateSelectedContent(item);
  //           return;
  //         default:
  //           throw new Error("Unhandled item type: ", selected);
  //       }
  //     }
  //   },
  //   [
  //     state.selectedAnthologies,
  //     state.selectedCollections,
  //     state.selectedContent,
  //     updateSelectedAnthologies,
  //     updateSelectedCollections,
  //     updateSelectedContent,
  //   ],
  // );

  const selectAllContent = useCallback(
    ({ checked, indeterminate }, contentList) => {
      console.log({ checked, indeterminate }, contentList);
      if ((checked && isEmpty(state.selectedContent)) || indeterminate) {
        const updated = filter(item => item.status !== "TAKEDOWN", contentList);

        dispatch({
          type: actionTypes.update_selected_content,
          selectedContent: updated,
        });
      } else {
        dispatch({
          type: actionTypes.update_selected_content,
          selectedContent: [],
        });
      }
    },
    [state.selectedContent],
  );

  const appendToSelectedContent = useCallback(
    (target, list) => {
      const updated =
        target.checked && !isEmpty(state.selectedContent)
          ? removeList(state.selectedContent, list)
          : target.checked
          ? []
          : deduplicate(concat(state.selectedContent, list));

      dispatch({
        type: actionTypes.update_selected_content,
        selectedContent: updated,
      });
    },
    [state.selectedContent],
  );

  const removeAllSelected = () => {
    removeAllSelectedContentGroups();
    removeAllSelectedContent();
  };

  const removeAllSelectedContent = useCallback(() => {
    dispatch({
      type: actionTypes.update_selected_content,
      selectedContent: [],
    });
  }, []);

  const updateCurrentCollectionContentList = useCallback(contentList => {
    dispatch({
      type: actionTypes.update_current_collection_content_list,
      currentCollectionContentList: contentList,
    });
  }, []);

  const updateExpanded = useCallback(expanded => {
    dispatch({
      type: actionTypes.update_expanded,
      expanded,
    });
  }, []);

  const updateSnackAlert = useCallback(alert => {
    dispatch({
      type: actionTypes.update_alert,
      alert: alert,
    });
  }, []);

  const resetSnackAlert = () => {
    dispatch({
      type: actionTypes.update_alert,
      alert: initialAlert,
    });
    closeAlert();
  };

  const context = {
    state,
    appendToSelectedContent,
    moveNotAllowed,
    removeAllSelected,
    removeAllSelectedContent,
    removeAllSelectedContentGroups,
    selectAllContent,
    selectAllContentGroups,
    showAlert,
    updateActiveSelectedMediaType,
    updateCurrentCollectionContentList,
    updateDragAction,
    updateDragSourceParent,
    updateDragSourceId,
    updateDropHandler,
    updateDropItems,
    updateDropTarget,
    updateExpanded,
    updateIncludeDialog,
    updateIsDragging,
    updateSelectedContent,
    updateSelectedContentGroups,
    updateSelectedContentGroupsWithList,
    updateSnackAlert,
  };

  return (
    <OrganizeContext.Provider value={context}>
      <SnackAlert
        action={
          state.alert.showAction && (
            <Button
              variant="outlined"
              style={{ color: "white" }}
              onClick={() => {
                state.alert.onAlertClick();
                resetSnackAlert();
              }}
            >
              {state.alert.actionButtonLabel}
            </Button>
          )
        }
      />
      {children}
    </OrganizeContext.Provider>
  );
};

const useAlert = () => {
  const { state, showAlert, updateSnackAlert } = useContext(OrganizeContext);
  return {
    state: state.alert,
    show: showAlert,
    update: updateSnackAlert,
  };
};

const useDnD = () => {
  const { state, updateDragAction } = useContext(OrganizeContext);

  return {
    state,
    updateDragAction,
  };
};

const useSidebar = () => {
  const { state, updateExpanded } = useContext(OrganizeContext);
  const { expanded } = state;

  return {
    expanded,
    updateExpanded,
  };
};

const useSelected = () => {
  const {
    state,
    appendToSelectedContent,
    removeAllSelected,
    removeAllSelectedContent,
    removeAllSelectedContentGroups,
    selectAllContent,
    selectAllContentGroups,
    updateActiveSelectedMediaType,
    updateSelectedContent,
    updateSelectedContentGroups,
    updateSelectedContentGroupsWithList,
  } = useContext(OrganizeContext);

  const {
    activeSelectedMediaType,
    selectedContent,
    selectedContentGroups,
  } = state;

  return {
    activeSelectedMediaType,
    appendToSelectedContent,
    removeAllSelected,
    removeAllSelectedContent,
    removeAllSelectedContentGroups,
    selectAllContent,
    selectAllContentGroups,
    selectedContent,
    selectedContentGroups,
    updateActiveSelectedMediaType,
    updateSelectedContent,
    updateSelectedContentGroups,
    updateSelectedContentGroupsWithList,
  };
};

export { OrganizeProvider, useAlert, useDnD, useSelected, useSidebar };
